import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Settings,
  Lock,
  Bell,
  Eye,
  EyeOff,
  Shield,
  Trash2,
  ArrowLeft,
  Save,
  AlertTriangle,
  CheckCircle2,
  Mail,
  MessageCircle,
  Smartphone,
  Globe,
  Zap,
  Clock,
  User,
  LogOut,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

// Role-specific settings
const ROLE_SETTINGS = {
  hospital: [
    { key: "order_approval_alerts", label: "Order Approval Alerts", desc: "Get notified when orders need approval", icon: CheckCircle2 },
    { key: "budget_threshold_notifications", label: "Budget Threshold Notifications", desc: "Alert when spending approaches limits", icon: AlertTriangle }
  ],
  contractor: [
    { key: "uber_dispatch_availability", label: "Available for Dispatch", desc: "Appear in the on-demand dispatch network", icon: Zap },
    { key: "telegram_bot_sync", label: "Telegram Bot Sync", desc: "Sync job updates with @askisaac_bot", icon: MessageCircle }
  ],
  supplier: [
    { key: "bid_win_alerts", label: "Bid Win Alerts", desc: "Instant notification when you win a bid", icon: CheckCircle2 },
    { key: "inventory_sync_frequency", label: "Inventory Sync", desc: "How often to sync inventory status", icon: RefreshCw, type: "select", options: ["hourly", "daily", "weekly"] }
  ],
  admin: [
    { key: "system_wide_markup_defaults", label: "Default Markup Settings", desc: "System-wide markup percentages", icon: Settings },
    { key: "global_notification_override", label: "Global Notification Override", desc: "Receive all system notifications", icon: Bell }
  ],
  super_admin: [
    { key: "system_wide_markup_defaults", label: "Default Markup Settings", desc: "System-wide markup percentages", icon: Settings },
    { key: "global_notification_override", label: "Global Notification Override", desc: "Receive all system notifications", icon: Bell },
    { key: "force_reactivation_access", label: "Force Reactivation Access", desc: "Override user deactivation status", icon: Shield }
  ]
};

export default function AccountSettingsPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("security");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    // Notification preferences
    email_notifications: true,
    telegram_notifications: true,
    sms_notifications: false,
    
    // Privacy settings
    public_profile: true,
    show_online_status: true,
    data_sharing: false,
    
    // Role-specific settings
    uber_dispatch_availability: true,
    telegram_bot_sync: true,
    order_approval_alerts: true,
    budget_threshold_notifications: true,
    bid_win_alerts: true,
    inventory_sync_frequency: "daily"
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [showPasswords, setShowPasswords] = useState(false);

  // Deactivation state
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivatePassword, setDeactivatePassword] = useState("");
  const [deactivating, setDeactivating] = useState(false);

  const userRole = user?.role || "hospital";
  const roleSettings = ROLE_SETTINGS[userRole] || ROLE_SETTINGS.hospital;

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/account/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/account/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success("Settings saved successfully!");
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Passwords don't match");
      return;
    }

    if (passwordData.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/account/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      if (response.ok) {
        toast.success("Password changed successfully!");
        setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to change password");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error("Error changing password");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateAccount = async () => {
    if (!deactivatePassword) {
      toast.error("Please enter your password to confirm");
      return;
    }

    setDeactivating(true);
    try {
      const response = await fetch(`${API}/account/deactivate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ password: deactivatePassword })
      });

      if (response.ok) {
        toast.success("Account deactivated. You can reactivate by logging in again.");
        setShowDeactivateModal(false);
        logout();
        navigate("/");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to deactivate account");
      }
    } catch (error) {
      console.error("Error deactivating:", error);
      toast.error("Error deactivating account");
    } finally {
      setDeactivating(false);
    }
  };

  const tabs = [
    { id: "security", label: "Security", icon: Lock },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "privacy", label: "Privacy", icon: Eye },
    { id: "preferences", label: "Preferences", icon: Settings }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/90 backdrop-blur-xl border-b border-slate-700/50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="font-bold text-lg">Account Settings</h1>
                <p className="text-xs text-slate-400">Security, notifications & privacy</p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save All"}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-cyan-600 text-white"
                    : "bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50"
                }`}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Security Tab */}
        {activeTab === "security" && (
          <div className="space-y-6">
            {/* Change Password */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-cyan-400" />
                Change Password
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-400">Current Password</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showPasswords ? "text" : "password"}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                      className="bg-slate-900 border-slate-700 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(!showPasswords)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400">New Password</Label>
                    <Input
                      type={showPasswords ? "text" : "password"}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                      className="mt-1 bg-slate-900 border-slate-700"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400">Confirm New Password</Label>
                    <Input
                      type={showPasswords ? "text" : "password"}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                      className="mt-1 bg-slate-900 border-slate-700"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleChangePassword}
                  disabled={saving || !passwordData.current_password || !passwordData.new_password}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  Update Password
                </Button>
              </div>
            </div>

            {/* Two-Factor Authentication */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-400" />
                Two-Factor Authentication
              </h3>
              <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                <div>
                  <p className="font-medium">Telegram 2FA</p>
                  <p className="text-sm text-slate-400">Verify login via @askisaac_bot</p>
                </div>
                <Switch
                  checked={settings.telegram_2fa || false}
                  onCheckedChange={(checked) => handleSettingChange("telegram_2fa", checked)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === "notifications" && (
          <div className="space-y-6">
            {/* Notification Channels */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-cyan-400" />
                Notification Channels
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="font-medium">Email Notifications</p>
                      <p className="text-sm text-slate-400">Receive updates via email</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.email_notifications}
                    onCheckedChange={(checked) => handleSettingChange("email_notifications", checked)}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <MessageCircle className="w-5 h-5 text-cyan-400" />
                    <div>
                      <p className="font-medium">Telegram Notifications</p>
                      <p className="text-sm text-slate-400">Instant alerts via @askisaac_bot</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.telegram_notifications}
                    onCheckedChange={(checked) => handleSettingChange("telegram_notifications", checked)}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Smartphone className="w-5 h-5 text-emerald-400" />
                    <div>
                      <p className="font-medium">SMS Notifications</p>
                      <p className="text-sm text-slate-400">Critical alerts via text message</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.sms_notifications}
                    onCheckedChange={(checked) => handleSettingChange("sms_notifications", checked)}
                  />
                </div>
              </div>
            </div>

            {/* Role-specific Notification Settings */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                Alert Preferences
              </h3>
              <div className="space-y-4">
                {roleSettings.filter(s => s.key.includes("alert") || s.key.includes("notification")).map((setting) => {
                  const SettingIcon = setting.icon;
                  return (
                    <div key={setting.key} className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <SettingIcon className="w-5 h-5 text-cyan-400" />
                        <div>
                          <p className="font-medium">{setting.label}</p>
                          <p className="text-sm text-slate-400">{setting.desc}</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings[setting.key] || false}
                        onCheckedChange={(checked) => handleSettingChange(setting.key, checked)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Privacy Tab */}
        {activeTab === "privacy" && (
          <div className="space-y-6">
            {/* Visibility Settings */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-cyan-400" />
                Profile Visibility
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div>
                    <p className="font-medium">Public Profile</p>
                    <p className="text-sm text-slate-400">Allow others to view your profile</p>
                  </div>
                  <Switch
                    checked={settings.public_profile}
                    onCheckedChange={(checked) => handleSettingChange("public_profile", checked)}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div>
                    <p className="font-medium">Show Online Status</p>
                    <p className="text-sm text-slate-400">Display when you're active on the platform</p>
                  </div>
                  <Switch
                    checked={settings.show_online_status}
                    onCheckedChange={(checked) => handleSettingChange("show_online_status", checked)}
                  />
                </div>
                {userRole === "contractor" && (
                  <div className="flex items-center justify-between p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                    <div>
                      <p className="font-medium text-emerald-400">Open for Dispatch</p>
                      <p className="text-sm text-slate-400">Appear in the on-demand dispatch network</p>
                    </div>
                    <Switch
                      checked={settings.uber_dispatch_availability}
                      onCheckedChange={(checked) => handleSettingChange("uber_dispatch_availability", checked)}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Data Sharing */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-violet-400" />
                Data Sharing
              </h3>
              <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                <div>
                  <p className="font-medium">Analytics & Improvements</p>
                  <p className="text-sm text-slate-400">Help us improve by sharing usage data</p>
                </div>
                <Switch
                  checked={settings.data_sharing}
                  onCheckedChange={(checked) => handleSettingChange("data_sharing", checked)}
                />
              </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-red-400">
                <AlertTriangle className="w-5 h-5" />
                Danger Zone
              </h3>
              <div className="p-4 bg-slate-900/50 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-red-400">Deactivate Account</p>
                    <p className="text-sm text-slate-400">
                      Temporarily disable your account. Your data will be preserved.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDeactivateModal(true)}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Deactivate
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preferences Tab */}
        {activeTab === "preferences" && (
          <div className="space-y-6">
            {/* Role-specific Settings */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-cyan-400" />
                {userRole.charAt(0).toUpperCase() + userRole.slice(1)} Settings
              </h3>
              <div className="space-y-4">
                {roleSettings.map((setting) => {
                  const SettingIcon = setting.icon;
                  return (
                    <div key={setting.key} className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <SettingIcon className="w-5 h-5 text-cyan-400" />
                        <div>
                          <p className="font-medium">{setting.label}</p>
                          <p className="text-sm text-slate-400">{setting.desc}</p>
                        </div>
                      </div>
                      {setting.type === "select" ? (
                        <Select
                          value={settings[setting.key] || setting.options[0]}
                          onValueChange={(value) => handleSettingChange(setting.key, value)}
                        >
                          <SelectTrigger className="w-32 bg-slate-800 border-slate-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {setting.options.map((opt) => (
                              <SelectItem key={opt} value={opt}>
                                {opt.charAt(0).toUpperCase() + opt.slice(1)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Switch
                          checked={settings[setting.key] || false}
                          onCheckedChange={(checked) => handleSettingChange(setting.key, checked)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Deactivation Modal */}
      <Dialog open={showDeactivateModal} onOpenChange={setShowDeactivateModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Deactivating Your Isaac Account
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              <div className="mt-4 p-4 bg-slate-800/50 rounded-lg text-left">
                <p className="mb-3">
                  Deactivation is <span className="text-white font-medium">temporary</span>. Your profile and jobs will be hidden from the network, 
                  but we will securely save your order history and certifications.
                </p>
                <p className="text-cyan-400">
                  Need a break? Deactivating keeps your data safe until you're ready to return.
                </p>
              </div>
              <div className="mt-4 space-y-2 text-left text-sm">
                <p className="flex items-center gap-2">
                  <EyeOff className="w-4 h-4 text-amber-400" />
                  Profile hidden from search & dispatch
                </p>
                <p className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-amber-400" />
                  All notifications suspended
                </p>
                <p className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  Data & history preserved for 30 days
                </p>
                <p className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-emerald-400" />
                  Reactivate anytime by logging in
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <Label className="text-slate-400">Enter your password to confirm</Label>
            <Input
              type="password"
              value={deactivatePassword}
              onChange={(e) => setDeactivatePassword(e.target.value)}
              placeholder="Your password"
              className="mt-2 bg-slate-800 border-slate-700"
            />
          </div>
          <DialogFooter className="mt-6">
            <Button
              variant="ghost"
              onClick={() => setShowDeactivateModal(false)}
              className="text-slate-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeactivateAccount}
              disabled={deactivating || !deactivatePassword}
              className="bg-red-600 hover:bg-red-700"
            >
              {deactivating ? "Deactivating..." : "Deactivate Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
